import { memo } from 'react'

import { cn } from '@/utils'

import { Cue } from './Cue'
import { useIsDraggingCue } from './cue-dnd.store'
import { CueDragHandleContext, useCueDraggable } from './cue.dnd.hooks'

/**
 * A cue row, made draggable.
 *
 * The wrapper is the draggable node while the grab handle lives inside the row
 * (see `useCueDragHandle`), so whole cue moves but only handle starts a drag.
 */
export const CueDraggable = memo(function CueDraggable({
  cueId,
}: {
  cueId: string
}) {
  const isDragging = useIsDraggingCue(cueId)
  const { attributes, setNodeRef, style, dragHandle } = useCueDraggable(cueId)

  return (
    <div
      {...attributes}
      ref={setNodeRef}
      style={style}
      className={cn(
        // We're using custom `DragOverlay`, so visually remove actual element while dragging
        isDragging && 'opacity-0',
      )}
      // Override all properties that affect ARIA structure.
      // The row is draggable, but we want a11y engines to ignore it and look at the child element instead.
      role="none"
      tabIndex={undefined}
      aria-disabled={undefined}
      aria-roledescription={undefined}
      aria-describedby={undefined}
    >
      <CueDragHandleContext.Provider value={dragHandle}>
        <Cue cueId={cueId} />
      </CueDragHandleContext.Provider>
    </div>
  )
})
